import { createSelector } from 'reselect';

import { createParameterSelector } from '@pressreader/appstore';
import { selectUser } from '@pressreader/authentication-core';

import { Comment } from '../../types/comment';
import { getState } from '../reducers';

import { selectCommentDraftId, selectIsLoaded } from './common';

const selectCommentsState = createSelector(getState, state => state.comments);

export const selectComments = createSelector(selectCommentsState, state => state.comments);
export const selectOffset = createSelector(selectCommentsState, state => state.offset);

export const selectIsLoading = createSelector(selectCommentsState, state => state.isLoadingPreviousPage || state.isLoadingNextPage);

export const selectIsLoadingPreviousPage = createSelector(selectCommentsState, state => state.isLoadingPreviousPage);
export const selectCanLoadPreviousPage = createSelector(
    selectCommentsState,
    selectIsLoaded,
    selectIsLoadingPreviousPage,
    (state, isLoaded, isLoadingPreviousPage) => isLoaded && state.offset > 0 && !isLoadingPreviousPage,
);

export const selectIsLoadingNextPage = createSelector(selectCommentsState, state => state.isLoadingNextPage);
export const selectCanLoadNextPage = createSelector(
    selectCommentsState,
    selectIsLoaded,
    selectIsLoadingNextPage,
    (state, isLoaded, isLoadingNextPage) =>
        isLoaded &&
        state.totalCommentsCount > state.comments.length &&
        state.rootCommentsCount > state.offset + state.comments.filter(c => !c.parentId).length &&
        !isLoadingNextPage,
);

const selectCommentId = createParameterSelector<'commentId', string>('commentId');

export const selectComment = createSelector(selectCommentId, selectComments, (commentId, comments) => comments.find(c => c.id === commentId));

export const selectCommentReplies = createSelector(selectCommentId, selectComments, (commentId, comments) =>
    comments.filter(c => c.parentId === commentId),
);

export const selectTotalCommentsCount = createSelector(selectCommentsState, state => state.totalCommentsCount);

export const selectIsCommentOwner = createSelector(selectCommentId, selectComments, selectUser, (commentId, comments: Comment[], user) => {
    const comment = comments.find(c => c.id === commentId);
    return comment?.userProfile.enAccountNumber === user.enAccountNumber;
});

export const selectNewCommentId = createSelector(selectCommentsState, state => state.newCommentId);
export const selectNewPostId = createSelector(selectCommentsState, state => state.newPostId);

export const selectIsCommentPosting = createSelector(
    selectCommentDraftId,
    selectCommentsState,
    (draftId, state) => state.isPostingDraftId === draftId,
);
